import { useCallback } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import {
  DropdownMenu,
  Eyebrow,
  Divider,
  Button,
  ButtonAppearance,
  Icon,
  SectionMessageAppearance,
  SectionMessage,
  Text,
} from '@atlassian/learning-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '@atlassian/community-library/dist/components/loading-spinner';

import { LiveTrainingCard } from 'src/components/LiveTrainingCard';
import { MobileFilterMenu } from 'src/components/MobileFilterMenu';
import { useGetLiveTraining } from 'src/api/hooks';

import { CalendarEvent, useLiveTrainingActions } from './hooks/useLiveTrainingActions';
import { useLiveTrainingFilters, useFilteredLiveTrainingSessions } from './hooks';
import { EMPTY_STATE_TYPES, EmptyStateType, LearningEmptyState } from './LearningEmptyStateV2';

export const LiveTrainingTab = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set second parameter to true to use mock data
  const { data: liveTrainingSessions = [], isLoading } = useGetLiveTraining(20);

  // Get filter state and handlers
  const filters = useLiveTrainingFilters(liveTrainingSessions);

  // Get filtered and sorted sessions
  const { upcomingSessions, pastSessions, showUpcoming, showPast } = useFilteredLiveTrainingSessions(
    liveTrainingSessions,
    {
      selectedStatusFilters: filters.selectedStatusFilters,
      selectedTypeFilters: filters.selectedTypeFilters,
      selectedDateFilters: filters.selectedDateFilters,
    },
  );

  const {
    isLearningHistoryLoading,
    isCompletionCertificateLoading,
    handleLearningHistoryClick,
    handleBrowseCalendar,
    handleDetailsClick,
    handleAddToCalendar,
    handleDownloadMaterials,
    handleDownloadCompletionCertificate,
    handleToggleSaved,
  } = useLiveTrainingActions({ liveTrainingData: liveTrainingSessions });

  // Handle applying filters from mobile filter menu
  const handleApplyFilters = useCallback(
    (filtersToApply: Record<string, string[]>): void => {
      const newSearchParams = new URLSearchParams(searchParams);

      // Clear existing filter params
      Object.keys(filtersToApply).forEach((key) => {
        newSearchParams.delete(key);
      });

      // Apply new filters
      Object.entries(filtersToApply).forEach(([key, values]) => {
        if (values.length > 0) {
          newSearchParams.set(key, values.join(','));
        }
      });

      // Update URL
      navigate({ search: newSearchParams.toString() });
    },
    [searchParams, navigate],
  );

  const hasItems = liveTrainingSessions.length > 0;
  const hasFilteredItems = upcomingSessions.length > 0 || pastSessions.length > 0;
  const emptyStateType: EmptyStateType =
    hasItems && !hasFilteredItems
      ? EMPTY_STATE_TYPES.LIVE_TRAINING_EMPTY_FILTER
      : EMPTY_STATE_TYPES.LIVE_TRAINING_EMPTY_RESULTS;
  const shouldShowEmptyState =
    (!showUpcoming || upcomingSessions.length === 0) && (!showPast || pastSessions.length === 0);

  return (
    <div css={containerStyles} data-testid="live-training-tab">
      {/* Mobile Filter Menu - shown on mobile only */}
      <div css={mobileFilterContainerStyles}>
        <MobileFilterMenu
          filterSections={filters.mobileFilterSections}
          selectedFiltersLength={filters.selectedFiltersLength}
          onApplyFilters={handleApplyFilters}
          onClearAll={filters.handleClearFilters}
          auxiliaryComponent={
            <Button appearance={ButtonAppearance.PRIMARY} label="Browse calendar" onClick={handleBrowseCalendar} />
          }
        />
      </div>

      {/* Desktop Controls Row - shown on desktop only */}
      <div css={[headerContainerStyles, !hasItems && css({ alignSelf: 'flex-end' }), desktopOnlyStyles]}>
        {hasItems && (
          <div css={filtersContainerStyles}>
            <Eyebrow size="medium" fontWeight="semibold">
              Filter by
            </Eyebrow>
            <DropdownMenu
              items={filters.statusFilterItems}
              onItemClick={(label) =>
                filters.handleStatusFilterChange(label, filters.selectedStatusFilters.includes(label))
              }
              placement="bottom-start"
              isCheckboxMenu={true}
              triggerText="Status"
            />

            <DropdownMenu
              items={filters.typeFilterItems}
              onItemClick={(label) =>
                filters.handleTypeFilterChange(label, filters.selectedTypeFilters.includes(label))
              }
              placement="bottom-start"
              isCheckboxMenu={true}
              triggerText="Type"
            />

            <DropdownMenu
              items={filters.dateFilterItems}
              onItemClick={(label) =>
                filters.handleDateFilterChange(label, filters.selectedDateFilters.includes(label))
              }
              placement="bottom-start"
              isCheckboxMenu={true}
              triggerText="Date"
            />
            {filters.hasActiveFilters && (
              <Button
                data-testid="clear-filters-button"
                label="Clear filters"
                appearance={ButtonAppearance.LINK}
                onClick={filters.handleClearFilters}
              />
            )}
          </div>
        )}

        <div css={historyAndCalendarButtonStyles}>
          <Button
            appearance={ButtonAppearance.SECONDARY}
            label="Learning history"
            iconBefore={<Icon type="download-core" color={token('color.icon.brand')} />}
            onClick={handleLearningHistoryClick}
            isLoading={isLearningHistoryLoading}
          />
          <Button appearance={ButtonAppearance.PRIMARY} label="Browse calendar" onClick={handleBrowseCalendar} />
        </div>
      </div>

      {hasItems && (
        <SectionMessage appearance={SectionMessageAppearance.INFORMATION_WHITE} fullWidth>
          <Text>Enrolled through a partner site? It may take some time for your class to appear.</Text>
        </SectionMessage>
      )}

      {isLoading && <LoadingSpinner />}

      {/* Upcoming Section */}
      {!isLoading && showUpcoming && upcomingSessions.length > 0 && (
        <div css={[sectionStyles, css({ paddingTop: token('space.200') })]}>
          <Eyebrow size="medium" fontWeight="semibold">
            Upcoming
          </Eyebrow>
          <Divider color={token('color.border')} />
          <div css={cardsGridStyles}>
            {upcomingSessions.map((training) => (
              <LiveTrainingCard
                key={training.instanceId}
                {...training}
                onDetailsClick={(): void => handleDetailsClick(training.courseId, training.instanceId)}
                onAddToCalendar={
                  training.showAddToCalendar
                    ? (calendarEvent: CalendarEvent): void => handleAddToCalendar(calendarEvent)
                    : undefined
                }
                onSave={(): void => handleToggleSaved(training.courseId, training.instanceId, training.isSaved)}
                onDownloadMaterials={
                  training.showDownloadMaterials
                    ? (): void => handleDownloadMaterials(training.courseMaterialsUrl)
                    : undefined
                }
                onDownloadCompletionCertificate={
                  training.status === 'completed'
                    ? (): Promise<void> => handleDownloadCompletionCertificate(training.title, training.endDateTime)
                    : undefined
                }
                showAddToCalendar
                showDownloadCompletionCertificate={training.status === 'completed'}
                isCompletionCertificateLoading={isCompletionCertificateLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Past Section */}
      {!isLoading && showPast && pastSessions.length > 0 && (
        <div css={sectionStyles}>
          <Eyebrow size="medium" fontWeight="semibold">
            Past
          </Eyebrow>
          <Divider color={token('color.border')} />
          <div css={cardsGridStyles}>
            {pastSessions.map((training) => (
              <LiveTrainingCard
                key={training.instanceId}
                {...training}
                onDetailsClick={(): void => handleDetailsClick(training.courseId, training.instanceId)}
                onAddToCalendar={
                  training.showAddToCalendar
                    ? (calendarEvent: CalendarEvent): void => handleAddToCalendar(calendarEvent)
                    : undefined
                }
                onSave={(): void => handleToggleSaved(training.courseId, training.instanceId, training.isSaved)}
                onDownloadMaterials={
                  training.showDownloadMaterials
                    ? (): void => handleDownloadMaterials(training.courseMaterialsUrl)
                    : undefined
                }
                onDownloadCompletionCertificate={
                  training.status === 'completed'
                    ? (): Promise<void> => handleDownloadCompletionCertificate(training.title, training.endDateTime)
                    : undefined
                }
                showAddToCalendar
                showDownloadCompletionCertificate={training.status === 'completed'}
                isCompletionCertificateLoading={isCompletionCertificateLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Show empty state when (upcoming section is hidden OR empty) AND (past section is hidden OR empty) */}
      {!isLoading && shouldShowEmptyState && <LearningEmptyState emptyStateType={emptyStateType} />}
    </div>
  );
};

const containerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),
  paddingTop: token('space.300'),
});

const headerContainerStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const filtersContainerStyles = css({
  display: 'flex',
  gap: token('space.150'),
  alignItems: 'center',
  flexWrap: 'wrap',

  [media.below.sm]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
});

const historyAndCalendarButtonStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: token('space.150'),

  // learning history button
  '& [data-testid="button-secondary"]': {
    height: 'fit-content',
    alignSelf: 'center',
    paddingTop: token('space.100'),
    paddingBottom: token('space.100'),

    '&:hover svg': {
      color: token('color.icon.inverse'),
    },
  },

  '& [data-testid="button-secondary"] svg': {
    color: token('color.icon.brand'),
    width: '21px',
    height: '21px',
  },
});

const sectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),

  [media.below.md]: {
    '& + &': {
      paddingTop: token('space.600'),
    },
    marginLeft: token('space.negative.200'),
    marginRight: token('space.negative.200'),

    // section heading (Upcoming or Past)
    '& h4': {
      paddingLeft: token('space.200'),
      paddingRight: token('space.200'),
    },
  },
});

const cardsGridStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),
  paddingTop: token('space.100'),

  [media.below.md]: {
    gap: token('space.400'),
  },
});

const mobileFilterContainerStyles = css({
  display: 'none',

  [media.below.sm]: {
    display: 'block',
  },
});

const desktopOnlyStyles = css({
  [media.below.sm]: {
    display: 'none',
  },
});
